/**
 * /txt-to-pdf and /text-to-pdf (SEO_BRIEF_08 sections 7.2 to 7.4): plain text made into a PDF in
 * the browser, as a document.
 *
 * THE CHAIN, all in this tab:
 *
 *   text  -->  parse in DOCUMENT MODE  -->  write PDF with columns ['text']
 *
 * The text comes from a dropped `.txt` (both pages) or the box (/text-to-pdf). A file is decoded
 * here with the detector every subtitle tool uses (`decodeBytes`, src/worker/adapter/encoding.ts),
 * and the "Read as" panel decodes it again in the encoding picked. The text then goes to the
 * worker as UTF-8 with `document: true` on the parse, so it is read as paragraphs
 * (src/formats/document/text-document.ts) instead of being timed and split like subtitles, and is
 * written by the PDF writer with the page's options (src/formats/pdf/options.ts).
 *
 * THE BYTES EXIST BEFORE THE BUTTON IS ENABLED. Saving a file after an await is what makes a
 * download fail silently in Safari, so every change (the text, a file, an encoding, an option)
 * writes the PDF again, and Download hands over bytes it already holds.
 *
 * Nothing leaves the browser but two counts (lib/usage.ts): `loaded` when text first arrives and
 * on each file, and `downloaded` plus `pdf_text_download` on each save.
 */

import type { DocumentId } from '../../../../../contracts/cue-model.ts';
import { characterCount } from '../../../../../src/formats/document/text-document.ts';
import {
  defaultPageSize,
  type PdfDocumentChoices,
  type PdfTextSize,
  pdfDocumentOptions,
} from '../../../../../src/formats/pdf/options.ts';
import { scanScripts } from '../../../../../src/formats/pdf/scripts.ts';
import { decodeBytes } from '../../../../../src/worker/adapter/encoding.ts';
import { data, on, qs, qsa } from '../lib/dom.ts';
import { saveBytes } from '../lib/download.ts';
import { mountDropzone } from '../lib/dropzone.ts';
import { fileSize, shortFilename } from '../lib/format.ts';
import { ensurePdfFonts, PdfFontLoadError } from '../lib/pdf-fonts.ts';
import { countUse } from '../lib/usage.ts';
import { isVersionMismatch, WorkerFailure, worker } from '../lib/worker.ts';
import { downloadWriteOptions } from '../lib/write-options.ts';

/** How long typing pauses before the PDF is written again. */
const TYPING_PAUSE_MS = 350;
/** How much of a file's text the preview shows, so a wrong encoding is visible at once. */
const PREVIEW_CHARS = 600;
/** The name a pasted text's PDF takes when it has no title. */
const PASTED_NAME = 'text';

type ErrorKey =
  | 'empty'
  | 'empty-file'
  | 'wrong-file'
  | 'unshapeable'
  | 'font'
  | 'font-load'
  | 'failed';

/** Whether a dropped file is one this page reads: a .txt, or a file the browser calls text. */
export function isTextFile(file: Pick<File, 'name' | 'type'>): boolean {
  if (/\.txt$/i.test(file.name)) return true;
  return file.type === 'text/plain';
}

/** The PDF's name: the file's, the title's, or "text". The worker makes it safe and adds .pdf. */
export function inputNameFor(fileName: string | null, title: string): string {
  if (fileName) return fileName;
  const fromTitle = title.replace(/\s+/g, ' ').trim().slice(0, 80);
  return `${fromTitle || PASTED_NAME}.txt`;
}

export function mountTextPdfPage(): void {
  const root = qs<HTMLElement>('[data-text-pdf]');
  if (!root) return;
  const box = data(root, 'text-pdf') === 'box';
  const tool = data(root, 'usage-tool') ?? (box ? 'text-to-pdf' : 'txt-to-pdf');

  const textarea = qs<HTMLTextAreaElement>('[data-text-pdf-text]', root);
  const count = qs<HTMLElement>('[data-text-pdf-count] [data-slot="count"]', root);
  const parsed = qs<HTMLElement>('[data-parsed-preview]', root);
  const preview = qs<HTMLElement>('[data-text-pdf-preview]', root);
  const encodingSelect = qs<HTMLSelectElement>('[data-encoding-override]', root);
  const encodingPanel = qs<HTMLDetailsElement>('[data-encoding-panel]', root);
  const button = qs<HTMLButtonElement>('[data-text-pdf-download]', root);
  const title = qs<HTMLInputElement>('[data-text-pdf-title]', root);
  const numbers = qs<HTMLInputElement>('[data-text-pdf-numbers]', root);
  const pageRadios = qsa<HTMLInputElement>('[data-text-pdf-page]', root);
  const typeRadios = qsa<HTMLInputElement>('[data-text-pdf-type]', root);
  const dropped = qs<HTMLElement>('[data-text-pdf-notice="dropped"]', root);

  // Letter for a browser set to US English, A4 everywhere else (section 7.3).
  const size = defaultPageSize(typeof navigator === 'undefined' ? [] : navigator.languages);
  for (const radio of pageRadios) radio.checked = radio.value === size;

  /** The file this text came from, kept so "Read as" can decode it again. */
  let file: { name: string; bytes: Uint8Array } | null = null;
  /** The text the PDF is made from. */
  let text = '';
  let counted = false;
  /** The PDF that Download saves, or null while there is none for the current text. */
  let output: { bytes: Uint8Array; filename: string } | null = null;
  /** Bumped on every change, so a write that a newer change overtook is dropped. */
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastDoc: DocumentId | null = null;

  const client = worker();

  function showError(key: ErrorKey | null): void {
    for (const el of qsa<HTMLElement>('[data-text-pdf-error]', root as HTMLElement)) {
      el.hidden = data(el, 'text-pdf-error') !== key;
    }
  }

  function choices(): PdfDocumentChoices {
    const page = pageRadios.find((r) => r.checked)?.value === 'letter' ? 'letter' : 'a4';
    const picked = typeRadios.find((r) => r.checked)?.value;
    const textSize: PdfTextSize = picked === 'small' || picked === 'large' ? picked : 'normal';
    return {
      pageSize: page,
      textSize,
      title: title?.value ?? '',
      pageNumbers: numbers?.checked ?? true,
    };
  }

  function setCount(): void {
    if (count) count.textContent = characterCount(text).toLocaleString();
  }

  function invalidate(): void {
    generation++;
    output = null;
    if (button) button.disabled = true;
  }

  /** Write the PDF again for the current text and options, after `delay` ms. */
  function schedule(delay = 0): void {
    invalidate();
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void write(generation);
    }, delay);
  }

  async function write(mine: number): Promise<void> {
    if (dropped) dropped.hidden = true;
    if (text.trim() === '') {
      // Nothing to make yet. Said only once someone has started: an empty box on arrival is
      // not an error.
      showError(file !== null ? 'empty-file' : counted ? 'empty' : null);
      return;
    }
    // The writer refuses these scripts with a reason written for developers; the page says it
    // in its own words, before any work.
    if (scanScripts([text]).blocking.length > 0) {
      showError('unshapeable');
      return;
    }
    showError(null);
    const options = pdfDocumentOptions(choices());
    try {
      await ensurePdfFonts(client, [text, options.header ?? '']);
      const name = inputNameFor(file?.name ?? null, options.header ?? '');
      const parsedDoc = await client.request('parse', {
        inputName: name,
        bytes: new TextEncoder().encode(text),
        encodingOverride: 'utf-8',
        document: true,
        retainRaw: false,
        retainBytes: false,
      });
      if (lastDoc !== null) client.dispose([lastDoc]);
      lastDoc = parsedDoc.docId;
      const result = await client.request('write', {
        docId: parsedDoc.docId,
        format: 'pdf',
        options: downloadWriteOptions('pdf', { formatOptions: options }),
      });
      if (mine !== generation) return;
      if (!(result.output instanceof Uint8Array)) throw new Error('the PDF writer returned text');
      output = { bytes: result.output, filename: result.filename };
      if (dropped) dropped.hidden = !result.loss.some((l) => l.code === 'characters-dropped');
      if (button) button.disabled = false;
    } catch (err) {
      if (mine !== generation) return;
      if (err instanceof PdfFontLoadError) {
        showError('font-load');
      } else if (err instanceof WorkerFailure && err.reason === 'font-unavailable') {
        showError('font');
      } else {
        if (isVersionMismatch(err)) console.error('[text-pdf]', err);
        else console.error('[text-pdf] write failed', err);
        showError('failed');
      }
    }
  }

  /** New text has arrived: count it once per page, then write. */
  function useText(next: string, delay = 0): void {
    text = next;
    setCount();
    if (!counted && next.trim() !== '') {
      counted = true;
      countUse('loaded', tool, 'txt');
    }
    schedule(delay);
  }

  /** Decode the file in `label`, or by detection when there is none, and use its text. */
  function readFile(label: string | null): void {
    if (file === null) return;
    const decoded = decodeBytes({
      bytes: file.bytes,
      ...(label === null ? {} : { userLabel: label }),
      filename: file.name,
    });
    if (parsed) {
      parsed.hidden = false;
      const set = (slot: string, value: string): void => {
        const el = qs<HTMLElement>(`[data-slot="${slot}"]`, parsed);
        if (el) el.textContent = value;
      };
      set('filename', shortFilename(file.name));
      set('size', fileSize(file.bytes.byteLength));
      set('encoding', decoded.encoding.label);
    }
    if (encodingSelect) encodingSelect.value = decoded.encoding.label;
    if (encodingPanel && label === null) encodingPanel.open = decoded.needsOverride;
    if (preview) {
      const head = decoded.text.slice(0, PREVIEW_CHARS);
      preview.textContent = decoded.text.length > PREVIEW_CHARS ? `${head}…` : head;
    }
    if (textarea) textarea.value = decoded.text;
    useText(decoded.text);
  }

  async function takeFiles(files: File[]): Promise<void> {
    const picked = files[0];
    if (!picked) return;
    if (!isTextFile(picked)) {
      invalidate();
      showError('wrong-file');
      countUse('failed', tool, 'unknown');
      return;
    }
    file = { name: picked.name, bytes: new Uint8Array(await picked.arrayBuffer()) };
    // A file is its own use, as on every tool page.
    counted = false;
    readFile(null);
  }

  /* ── wiring ─────────────────────────────────────────────────────────── */

  const host = qs<HTMLElement>('[data-dropzone-host]', root);
  if (host) mountDropzone(host, { onFiles: (files) => void takeFiles(files) });

  if (textarea) {
    on(textarea, 'input', () => {
      // Typed text is no longer the file it may have started as: "Read as" would undo the edit.
      if (file !== null) {
        file = null;
        if (parsed) parsed.hidden = true;
      }
      useText(textarea.value, TYPING_PAUSE_MS);
    });
  }
  if (encodingSelect) {
    on(encodingSelect, 'change', () => {
      readFile(encodingSelect.value);
    });
  }
  for (const radio of [...pageRadios, ...typeRadios]) on(radio, 'change', () => schedule());
  if (numbers) on(numbers, 'change', () => schedule());
  if (title) on(title, 'input', () => schedule(TYPING_PAUSE_MS));

  if (button) {
    on(button, 'click', () => {
      // Synchronous from here to the anchor click (see the note at the top).
      if (output === null) return;
      saveBytes(output.bytes, output.filename, 'pdf');
      countUse('downloaded', tool, 'pdf');
      countUse('pdf_text_download', tool, 'pdf');
    });
  }

  // Text the browser kept in the box across a reload.
  if (textarea && textarea.value !== '') useText(textarea.value);
}
